import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { baseURL } from '../data/config';

type SocketContextType = {
  socket: Socket | null;
  clientId: string | null;
  isConnected: boolean;
  connect: (token: string) => void;
  disconnect: () => void;
};

const SocketContext = createContext<SocketContextType | null>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [clientId, setClientId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  // Extract the root URL without /api-gateway path
  const socketURL = baseURL.replace(/\/api-gateway\/?$/, '');

  const connect = (token: string) => {
    // Initialize Socket.IO connection to /events namespace

    if (socketRef.current && socketRef.current.connected) return;

    const socket = io(`${socketURL}/events`, {
      path: '/socket.io',
      auth: { token },
      query: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      forceNew: false, // Reuse existing connection
    });

    socketRef.current = socket;

    // Handle successful connection to namespace
    socket.on('connect', () => {
      console.log(`✓ Connected to /events namespace, socket id: ${socket.id}`);
      setIsConnected(true);
    });

    // Handle connection-established event with clientId
    socket.on('connection-established', (data) => {
      console.log('✓ Connection established:', data);
      setClientId(data.clientId);
    });

    // Handle connection error from server
    socket.on('connection-error', (error) => {
      console.error('✗ Connection error:', error);
    });

    // Handle Socket.IO connect_error
    socket.on('connect_error', (error) => {
      console.error('✗ Socket connect_error:', error.message);
      setIsConnected(false);
    });

    // Handle disconnect
    socket.on('disconnect', (reason) => {
      console.log(`✗ Disconnected: ${reason}`);
      if (isConnected) setIsConnected(false);
      setClientId(null);
    });
  };

  const disconnect = () => {
    if (!socketRef.current) return;
    socketRef.current.disconnect();
    setIsConnected(false);
  };

  useEffect(() => {
    // Clean up listeners and disconnect only on unmount.
    return () => {
      if (socketRef.current) {
        socketRef.current.off('connect');
        socketRef.current.off('connection-established');
        socketRef.current.off('connection-error');
        socketRef.current.off('connect_error');
        socketRef.current.off('disconnect');
        socketRef.current.disconnect();

        socketRef.current = null;
      }
    };
  }, []); // run only on unmount

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        clientId,
        isConnected,
        disconnect,
        connect,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) throw Error('Socket context should be used within app context');
  return context;
};
